/**
 * Points Service — loyalty points a guest earns for using the request features.
 *
 * v1 (display only, no redemption): +1 for each song request, +1 per vote (up or
 * down), capped at VOTE_DAILY_CAP vote-points per day. Stored as a running
 * counter on the guest's own user doc plus an append-only ledger (audit + cap).
 *
 * Firestore (project chatbout001, all under `db`):
 *   users/{uid}.points              - running total (int), shown on the profile
 *                                     (combined across every venue)
 *   users/{uid}/pointsLedger/{id}   - one doc per earned point:
 *       { action:'request'|'vote', delta:1, venueUid, trackId, date:'YYYY-MM-DD',
 *         voteDate?, at }   (voteDate only on vote docs → single-field cap query,
 *                            no composite index needed)
 *   venuePoints/{venueUid}__{uid}   - PER-CHANNEL balance, so each venue tracks
 *       its own guests' points/prizes:
 *       { venueUid, uid, points, lifetimeEarned, redeemedTiers:[], lastEarnedAt }
 *       points = spendable balance at that venue; lifetimeEarned only ever
 *       grows (the "previous points" history the venue dashboards show).
 *
 * Every function swallows errors and resolves (never rejects) so a points
 * failure can never break a request or a vote. Written client-side like
 * tips/mood, so the counter is forgeable — fine for display-only v1, but the
 * ledger must move to a Cloud Function before any redemption is added.
 */

import {
  doc,
  collection,
  getDoc,
  serverTimestamp,
  increment,
  writeBatch,
  query,
  where,
  getCountFromServer,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase-config.js';

export const VOTE_DAILY_CAP = 20;

/**
 * The points regime — how much each guest action earns. ONE place to tune;
 * the ledger stamps the delta so history stays honest if values change later.
 * (Current values = the confirmed v1 rules: flat 1/1. The richer regime from
 * the full loyalty spec plugs in here — add actions / change values only.)
 */
export const POINT_VALUES = {
  request: 1, // requesting a song
  vote: 1,    // voting a track up or down (capped per night)
};

/** UTC day key 'YYYY-MM-DD' — matches tipService.tipTodayKey / MoodSelector.getTodayDate. */
export const pointsTodayKey = () => new Date().toISOString().split('T')[0];

/**
 * Add the per-channel balance write to an award batch. The deterministic doc
 * id venuePoints/{venueUid}__{uid} means the venue's dashboard can query all
 * its guests with a single where('venueUid'==…) — no collection-group index.
 */
const addVenuePointsWrite = (batch, { uid, venueUid, delta }) => {
  if (!venueUid) return;
  batch.set(
    doc(db, 'venuePoints', `${venueUid}__${uid}`),
    {
      venueUid,
      uid,
      points: increment(delta),
      lifetimeEarned: increment(delta),
      lastEarnedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

/**
 * Award +1 for a song request: one ledger doc (auto id) + increment the running
 * counter, atomically. Every request is a distinct point.
 * @returns {Promise<{ok:boolean, awarded?:boolean}>}
 */
export const awardRequestPoint = async ({ uid, venueUid, trackId } = {}) => {
  if (!uid) return { ok: false };
  try {
    const delta = POINT_VALUES.request;
    const batch = writeBatch(db);
    const ledgerRef = doc(collection(db, 'users', uid, 'pointsLedger'));
    batch.set(ledgerRef, {
      action: 'request',
      delta,
      venueUid: venueUid || null,
      trackId: trackId || null,
      date: pointsTodayKey(),
      at: serverTimestamp(),
    });
    batch.set(doc(db, 'users', uid), { points: increment(delta) }, { merge: true });
    addVenuePointsWrite(batch, { uid, venueUid, delta });
    await batch.commit();
    return { ok: true, awarded: true };
  } catch (err) {
    console.warn('[pointsService] awardRequestPoint failed:', err);
    return { ok: false };
  }
};

/**
 * Award +1 for a vote. Idempotent per track — a deterministic ledger id means a
 * track earns at most once ever (up/down switch, re-vote, double-tap all no-op).
 * Capped at VOTE_DAILY_CAP awarded vote-points per UTC day (the vote itself is
 * still recorded by the caller even when the point is skipped).
 * @returns {Promise<{ok:boolean, awarded:boolean, reason?:string}>}
 */
export const awardVotePoint = async ({ uid, venueUid, trackId } = {}) => {
  if (!uid || !trackId) return { ok: false, awarded: false };
  const voteRef = doc(db, 'users', uid, 'pointsLedger', 'vote_' + trackId);
  try {
    const existing = await getDoc(voteRef);
    if (existing.exists()) return { ok: true, awarded: false, reason: 'already' };

    const today = pointsTodayKey();
    // Count today's awarded votes — single-field query on voteDate (auto-indexed).
    try {
      const cnt = await getCountFromServer(
        query(collection(db, 'users', uid, 'pointsLedger'), where('voteDate', '==', today))
      );
      if ((cnt.data().count || 0) >= VOTE_DAILY_CAP) {
        return { ok: true, awarded: false, reason: 'capped' };
      }
    } catch (countErr) {
      // Permissive on a count/index hiccup — never silently block earning.
      console.warn('[pointsService] vote cap count failed, awarding anyway:', countErr);
    }

    const delta = POINT_VALUES.vote;
    const batch = writeBatch(db);
    batch.set(voteRef, {
      action: 'vote',
      delta,
      venueUid: venueUid || null,
      trackId,
      date: today,
      voteDate: today,
      at: serverTimestamp(),
    });
    batch.set(doc(db, 'users', uid), { points: increment(delta) }, { merge: true });
    addVenuePointsWrite(batch, { uid, venueUid, delta });
    await batch.commit();
    return { ok: true, awarded: true };
  } catch (err) {
    console.warn('[pointsService] awardVotePoint failed:', err);
    return { ok: false, awarded: false };
  }
};

/**
 * Live-subscribe to a guest's running points total. Calls cb(total:number).
 * Returns an unsubscribe function.
 */
export const subscribePoints = (uid, cb) => {
  if (!uid) { cb(0); return () => {}; }
  return onSnapshot(
    doc(db, 'users', uid),
    (snap) => cb(snap.exists() ? (snap.data().points || 0) : 0),
    (err) => { console.warn('[pointsService] subscribePoints error:', err); cb(0); }
  );
};

/**
 * Live-subscribe to a guest's loyalty state: running points total + the tiers
 * they've redeemed (badges). Points are cumulative and NEVER deducted; a
 * redeemed tier awards a badge. Calls cb({ points:number, redeemedTiers:number[] }).
 * Returns an unsubscribe function.
 */
export const subscribeRewards = (uid, cb) => {
  if (!uid) { cb({ points: 0, redeemedTiers: [] }); return () => {}; }
  return onSnapshot(
    doc(db, 'users', uid),
    (snap) => {
      const d = snap.exists() ? snap.data() : {};
      cb({
        points: Number(d.points) || 0,
        redeemedTiers: Array.isArray(d.redeemedTiers) ? d.redeemedTiers.map(Number) : [],
      });
    },
    (err) => { console.warn('[pointsService] subscribeRewards error:', err); cb({ points: 0, redeemedTiers: [] }); }
  );
};
